import { Modal, BlockStack, Text, List, Divider } from "@shopify/polaris";
import { useTranslation } from "../../hooks/useTranslation";

interface TermsOfServiceModalProps {
  open: boolean;
  onClose: () => void;
}

export function TermsOfServiceModal({ open, onClose }: TermsOfServiceModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("footer.terms")}
      primaryAction={{
        content: t("common.close") || "Close",
        onAction: onClose,
      }}
      size="large"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p" variant="bodyMd">
            <Text as="span" fontWeight="semibold">Effective Date:</Text> January 16, 2026
          </Text>

          <Text as="p" variant="bodyMd">
            These Terms of Service ("Terms") govern your use of the myDATA Comply application ("App" or "Service"). By installing or using the App, you agree to these Terms.
          </Text>

          <Divider />

          <Text as="h2" variant="headingMd">
            1. Acceptance of Terms
          </Text>
          <Text as="p" variant="bodyMd">
            By installing the myDATA Comply Shopify app, you ("Merchant", "you", or "your") agree to be bound by these Terms. If you do not agree to these Terms, do not install or use the App.
          </Text>

          <Divider />

          <Text as="h2" variant="headingMd">
            2. Service Description
          </Text>
          <Text as="p" variant="bodyMd">
            myDATA Comply automates the submission of delivery notes (παραστατικά αποστολής) to the Greek tax authority (AADE) through the myDATA API. The Service includes:
          </Text>
          <List type="bullet">
            <List.Item>Electronic submission of delivery notes to myDATA</List.Item>
            <List.Item>Generation of branded PDF delivery notes</List.Item>
            <List.Item>Submission history and audit trail</List.Item>
            <List.Item>Quota management and billing</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            3. Subscription and Billing
          </Text>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              3.1 Trial Period
            </Text>
            <Text as="p" variant="bodyMd">
              New merchants receive 7 free submissions during their trial period. No payment information is required for the trial.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              3.2 Paid Subscription
            </Text>
            <Text as="p" variant="bodyMd">
              After the trial period or when the free quota is exhausted, merchants must subscribe to one of the following plans, billed every 30 days through Shopify:
            </Text>
            <List type="bullet">
              <List.Item><Text as="span" fontWeight="semibold">Comply Starter</Text> — €9.99 for 30 submissions per billing cycle</List.Item>
              <List.Item><Text as="span" fontWeight="semibold">Comply Professional</Text> — €14.99 for 60 submissions per billing cycle</List.Item>
              <List.Item><Text as="span" fontWeight="semibold">Comply Growth</Text> — €29.99 for 300 submissions per billing cycle</List.Item>
            </List>
            <Text as="p" variant="bodyMd">
              Submissions apply to the billing cycle in which they are included and do not roll over. Upgrading during a billing cycle adds the new plan's submissions to the current cycle. Successful submissions, cancellations and credit documents each count as one submission; failed submissions do not.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              3.3 Billing
            </Text>
            <Text as="p" variant="bodyMd">
              Subscription fees are billed through Shopify's recurring charges system. By subscribing, you authorize Shopify to charge your payment method on file.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              3.4 Cancellation
            </Text>
            <Text as="p" variant="bodyMd">
              You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. No refunds are provided for partial billing periods.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            4. User Responsibilities
          </Text>
          <Text as="p" variant="bodyMd">
            As a Merchant using the Service, you are responsible for:
          </Text>
          <List type="bullet">
            <List.Item>Providing accurate myDATA API credentials and business information</List.Item>
            <List.Item>Ensuring compliance with Greek tax laws and regulations</List.Item>
            <List.Item>Verifying the accuracy of submitted delivery notes</List.Item>
            <List.Item>Maintaining the security of your Shopify and myDATA accounts</List.Item>
            <List.Item>Using the Service only for lawful purposes</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            5. Prohibited Uses
          </Text>
          <Text as="p" variant="bodyMd">
            You may not use the Service to:
          </Text>
          <List type="bullet">
            <List.Item>Submit false or fraudulent information to the Greek tax authority</List.Item>
            <List.Item>Violate any applicable laws or regulations</List.Item>
            <List.Item>Interfere with or disrupt the Service or servers</List.Item>
            <List.Item>Attempt to gain unauthorized access to the Service or related systems</List.Item>
            <List.Item>Resell or redistribute the Service without permission</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            6. Data and Privacy
          </Text>
          <Text as="p" variant="bodyMd">
            Your use of the Service is also governed by our Privacy Policy. By using the Service, you consent to our collection, use, and sharing of your data as described in the Privacy Policy.
          </Text>
          <Text as="p" variant="bodyMd">
            We collect and process merchant business information, customer shipping details, and order data as necessary to provide the Service and comply with Greek tax law.
          </Text>

          <Divider />

          <Text as="h2" variant="headingMd">
            7. Service Availability
          </Text>
          <Text as="p" variant="bodyMd">
            We strive to maintain high availability of the Service, but we do not guarantee uninterrupted access. The Service may be unavailable due to:
          </Text>
          <List type="bullet">
            <List.Item>Scheduled maintenance</List.Item>
            <List.Item>Emergency repairs</List.Item>
            <List.Item>Issues with third-party services (Shopify, myDATA API, Supabase)</List.Item>
            <List.Item>Force majeure events</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            8. Limitation of Liability
          </Text>
          <Text as="p" variant="bodyMd">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW:
          </Text>
          <List type="bullet">
            <List.Item>The Service is provided "AS IS" without warranties of any kind</List.Item>
            <List.Item>We are not liable for any tax penalties, fines, or legal issues arising from your use of the Service</List.Item>
            <List.Item>We are not responsible for errors in data submitted to myDATA caused by incorrect configuration or merchant input</List.Item>
            <List.Item>Our total liability for any claims shall not exceed the amount you paid for the Service in the 3 months prior to the claim</List.Item>
            <List.Item>We are not liable for indirect, incidental, consequential, or punitive damages</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            9. Indemnification
          </Text>
          <Text as="p" variant="bodyMd">
            You agree to indemnify and hold harmless myDATA Comply and its affiliates from any claims, damages, losses, or expenses (including legal fees) arising from:
          </Text>
          <List type="bullet">
            <List.Item>Your use of the Service</List.Item>
            <List.Item>Your violation of these Terms</List.Item>
            <List.Item>Your violation of any applicable laws or regulations</List.Item>
            <List.Item>Inaccurate information you provide through the Service</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            10. Termination
          </Text>
          <Text as="p" variant="bodyMd">
            We reserve the right to suspend or terminate your access to the Service at any time for:
          </Text>
          <List type="bullet">
            <List.Item>Violation of these Terms</List.Item>
            <List.Item>Non-payment of subscription fees</List.Item>
            <List.Item>Fraudulent or illegal use of the Service</List.Item>
            <List.Item>Any other reason at our discretion</List.Item>
          </List>
          <Text as="p" variant="bodyMd">
            Upon termination, your access to the Service will cease, but data retention will follow our Privacy Policy.
          </Text>

          <Divider />

          <Text as="h2" variant="headingMd">
            11. Changes to Terms
          </Text>
          <Text as="p" variant="bodyMd">
            We may update these Terms from time to time. Material changes will be communicated through the App or via email. Continued use of the Service after changes constitutes acceptance of the new Terms.
          </Text>

          <Divider />

          <Text as="h2" variant="headingMd">
            12. Governing Law
          </Text>
          <Text as="p" variant="bodyMd">
            These Terms are governed by the laws of Greece. Any disputes arising from these Terms or the Service shall be resolved in the courts of Greece.
          </Text>

          <Divider />

          <Text as="h2" variant="headingMd">
            13. Contact
          </Text>
          <Text as="p" variant="bodyMd">
            For questions about these Terms, please contact us at: lefteris@weam.gr
          </Text>

          <Divider />

          <Text as="p" variant="bodySm" tone="subdued">
            Last updated: October 1, 2026
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
